import Joi from 'joi';

import { TaxRateTypeEnum } from '../../../../shared/enums/tax-rate-type.enum';
import { createTagsSchema } from '../../../tags/validators/schemas/create-tags.schema';
import { TransactionRecordCategoryEnum } from '../../enums/transaction-record-category.enum';
import { TransactionTypeEnum } from '../../enums/transaction-type.enum';
import { getAllowedTransactionTypesForCategory } from '../../functions/get-allowed-transaction-types-for-category.function';
import { isPaymentBearingTransactionType } from '../../functions/is-payment-bearing-transaction-type.function';

/**
 * Schema for existing transaction record validation.
 */
export const updateTransactionSchema = () =>
  Joi.object({
    id: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid': 'validations|transaction-id|cannot-be-null',
      }),
      otherwise: Joi.number().required().messages({
        'any.required': 'validations|transaction-id|required',
        'number.base': 'validations|transaction-id|must-be-number',
      }),
    }),

    recordCategory: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid': 'validations|transaction-record-category|cannot-be-null',
      }),
      otherwise: Joi.string()
        .valid(...Object.values(TransactionRecordCategoryEnum))
        .required()
        .messages({
          'any.required': 'validations|transaction-record-category|required',
          'any.only': 'validations|transaction-record-category|invalid-value',
          'string.base':
            'validations|transaction-record-category|must-be-string',
          'string.empty':
            'validations|transaction-record-category|cannot-be-empty',
        }),
    }),

    type: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid': 'validations|transaction-type|cannot-be-null',
      }),
      otherwise: Joi.string()
        .required()
        .custom((value, helpers) => {
          const recordCategory = helpers.state.ancestors[0]?.recordCategory;
          const allowed = getAllowedTransactionTypesForCategory(
            recordCategory as TransactionRecordCategoryEnum,
          );
          if (!allowed.includes(value as TransactionTypeEnum)) {
            return helpers.error('any.invalid');
          }
          return value;
        })
        .messages({
          'any.required': 'validations|transaction-type|required',
          'any.invalid': 'validations|transaction-type|invalid-for-category',
          'string.base': 'validations|transaction-type|must-be-string',
          'string.empty': 'validations|transaction-type|cannot-be-empty',
        }),
    }),

    amount: Joi.number().positive().required().messages({
      'any.required': 'validations|transaction-amount|required',
      'number.base': 'validations|transaction-amount|must-be-number',
      'number.positive': 'validations|transaction-amount|must-be-positive',
    }),

    totalNet: Joi.number().min(0).optional().allow(null).messages({
      'number.base': 'validations|transaction-total-net|must-be-number',
      'number.min': 'validations|transaction-total-net|must-be-non-negative',
    }),
    totalTax: Joi.number().min(0).optional().allow(null).messages({
      'number.base': 'validations|transaction-total-tax|must-be-number',
      'number.min': 'validations|transaction-total-tax|must-be-non-negative',
    }),
    totalGross: Joi.number().min(0).optional().allow(null).messages({
      'number.base': 'validations|transaction-total-gross|must-be-number',
      'number.min': 'validations|transaction-total-gross|must-be-non-negative',
    }),

    taxRateType: Joi.when('type', {
      is: (val: string) =>
        !isPaymentBearingTransactionType(val as TransactionTypeEnum),
      then: Joi.string()
        .valid(...Object.values(TaxRateTypeEnum))
        .empty(null)
        .default(TaxRateTypeEnum.STANDARD_TAX_RATE),
      otherwise: Joi.string()
        .valid(...Object.values(TaxRateTypeEnum))
        .optional()
        .allow(null),
    }),
    taxRate: Joi.when('type', {
      is: (val: string) =>
        !isPaymentBearingTransactionType(val as TransactionTypeEnum),
      then: Joi.number().min(0).max(100).empty(null).default(0),
      otherwise: Joi.number().min(0).max(100).optional().allow(null),
    }),
    isTaxIncluded: Joi.when('type', {
      is: (val: string) =>
        !isPaymentBearingTransactionType(val as TransactionTypeEnum),
      then: Joi.boolean().empty(null).default(true),
      otherwise: Joi.boolean().optional().allow(null),
    }),

    rentalAgreementId: Joi.when('recordCategory', {
      is: [TransactionRecordCategoryEnum.RENTAL_AGREEMENT],
      then: Joi.alternatives().conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|transaction-rental-agreement-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required':
            'validations|transaction-rental-agreement-id|required',
          'number.base':
            'validations|transaction-rental-agreement-id|must-be-number',
        }),
      }),
      otherwise: Joi.allow(null),
    }),

    rentalReservationId: Joi.when('recordCategory', {
      is: [TransactionRecordCategoryEnum.RENTAL_RESERVATION],
      then: Joi.alternatives().conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|transaction-rental-reservation-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required':
            'validations|transaction-rental-reservation-id|required',
          'number.base':
            'validations|transaction-rental-reservation-id|must-be-number',
        }),
      }),
      otherwise: Joi.allow(null),
    }),

    vehicleInsuranceId: Joi.when('recordCategory', {
      is: TransactionRecordCategoryEnum.VEHICLE_INSURANCE,
      then: Joi.alternatives().conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|transaction-vehicle-insurance-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required':
            'validations|transaction-vehicle-insurance-id|required',
          'number.base':
            'validations|transaction-vehicle-insurance-id|must-be-number',
        }),
      }),
      otherwise: Joi.allow(null),
    }),

    vehicleMaintenanceId: Joi.when('recordCategory', {
      is: TransactionRecordCategoryEnum.VEHICLE_MAINTENANCE,
      then: Joi.alternatives().conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|transaction-vehicle-maintenance-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required':
            'validations|transaction-vehicle-maintenance-id|required',
          'number.base':
            'validations|transaction-vehicle-maintenance-id|must-be-number',
        }),
      }),
      otherwise: Joi.allow(null),
    }),

    vehicleRoadWorthyCheckId: Joi.when('recordCategory', {
      is: TransactionRecordCategoryEnum.VEHICLE_ROAD_WORTHY_CHECK,
      then: Joi.alternatives().conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|transaction-vehicle-road-worthy-check-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required':
            'validations|transaction-vehicle-road-worthy-check-id|required',
          'number.base':
            'validations|transaction-vehicle-road-worthy-check-id|must-be-number',
        }),
      }),
      otherwise: Joi.allow(null),
    }),

    vehicleRoadTaxId: Joi.when('recordCategory', {
      is: TransactionRecordCategoryEnum.VEHICLE_ROAD_TAX,
      then: Joi.alternatives().conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|transaction-vehicle-road-tax-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required':
            'validations|transaction-vehicle-road-tax-id|required',
          'number.base':
            'validations|transaction-vehicle-road-tax-id|must-be-number',
        }),
      }),
      otherwise: Joi.allow(null),
    }),

    agentId: Joi.when('recordCategory', {
      is: TransactionRecordCategoryEnum.AGENT,
      then: Joi.alternatives().conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid': 'validations|transaction-agent-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required': 'validations|transaction-agent-id|required',
          'number.base': 'validations|transaction-agent-id|must-be-number',
        }),
      }),
      otherwise: Joi.allow(null),
    }),

    customerId: Joi.when('recordCategory', {
      is: TransactionRecordCategoryEnum.CUSTOMER,
      then: Joi.alternatives().conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid': 'validations|transaction-customer-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required': 'validations|transaction-customer-id|required',
          'number.base': 'validations|transaction-customer-id|must-be-number',
        }),
      }),
      otherwise: Joi.allow(null),
    }),

    vehicleId: Joi.when('recordCategory', {
      is: TransactionRecordCategoryEnum.VEHICLE,
      then: Joi.alternatives().conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid': 'validations|transaction-vehicle-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required': 'validations|transaction-vehicle-id|required',
          'number.base': 'validations|transaction-vehicle-id|must-be-number',
        }),
      }),
      otherwise: Joi.allow(null),
    }),

    supplierId: Joi.when('recordCategory', {
      is: TransactionRecordCategoryEnum.SUPPLIER,
      then: Joi.alternatives().conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid': 'validations|transaction-supplier-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required': 'validations|transaction-supplier-id|required',
          'number.base': 'validations|transaction-supplier-id|must-be-number',
        }),
      }),
      otherwise: Joi.allow(null),
    }),

    locationId: Joi.when('recordCategory', {
      is: TransactionRecordCategoryEnum.LOCATION,
      then: Joi.alternatives().conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid': 'validations|transaction-location-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required': 'validations|transaction-location-id|required',
          'number.base': 'validations|transaction-location-id|must-be-number',
        }),
      }),
      otherwise: Joi.allow(null),
    }),

    paymentId: Joi.number().optional().allow(null),

    description: Joi.string().allow(null, '').messages({
      'string.base': 'validations|transaction-description|must-be-string',
    }),

    notes: Joi.string().allow(null, '').messages({
      'string.base': 'validations|transaction-notes|must-be-string',
    }),

    externalReference: Joi.string().allow(null, '').optional().messages({
      'string.base':
        'validations|transaction-external-reference|must-be-string',
    }),

    // Tags
    tags: createTagsSchema().messages({
      'array.base': 'validations|transaction-tags|invalid-value',
    }),

    files: Joi.any().allow(null),
  })
    // Ignore extra fields
    .unknown();
